import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const LABEL_CLASSES = {
  blue: 'bg-blue-700 text-white',
  pink: 'text-black bg-pink-400',
  gold: 'text-black bg-gold-400',
  lightBlue: 'text-black bg-blue-400',
  grey: 'text-black bg-grey-500',
  green: 'text-white bg-green-400',
};

function DevelopmentCard({
  id,
  name,
  slug,
  url,
  address,
  min_price,
  max_price,
  bedrooms,
  thumbnail,
  thumbnail_retina,
  thumbnail_meta = {},
  coins_id,
  label_text,
  label_colour,
  sticker_image,
}) {
  const labelClass = LABEL_CLASSES[label_colour] || LABEL_CLASSES.blue;

  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <div data-development={slug} className="flex flex-col h-full">
      <div className="flex flex-col flex-1 lg:items-center h-full">
        <div className="relative">
          <div className="h-full w-full text-white">
            {sticker_image && (
              <img
                data-src={sticker_image.url}
                alt={sticker_image.alt}
                height={32}
                width={32}
                class="lazyload absolute top-5 right-5 z-10 w-23"
              />
            )}

            <div className="absolute bottom-5 left-5 z-10">
              <div
                className={`font-medium rounded-[2px] text-xs uppercase pt-1.25 pb-1 px-2 text-center !tracking-wider ${labelClass}`}
              >
                {label_text}
              </div>
            </div>

            <div>
              <div className="max-h-104 ">
                <a href={url} aria-label={thumbnail_meta?.alt} className="block">
                  <AnimatePresence>
                    {!imgLoaded && (
                      <motion.div
                        key="img-fade-placeholder"
                        initial={{ opacity: 1 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: '#f3f4f6',
                          zIndex: 1,
                        }}
                      />
                    )}
                    <motion.img
                      key="img-fade"
                      className="lg:w-[598px] lg:h-full aspect-[181/105] object-cover object-center"
                      src={thumbnail}
                      srcSet={`${thumbnail} 1x, ${
                        thumbnail_retina || thumbnail
                      } 2x`}
                      sizes="(min-width: 1024px) 598px, 100vw"
                      width="598"
                      height="347"
                      alt={thumbnail_meta?.alt}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: imgLoaded ? 1 : 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      onLoad={() => setImgLoaded(true)}
                      style={{ position: 'relative', zIndex: 2 }}
                    />
                  </AnimatePresence>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 py-11 bg-white w-full flex flex-col flex-1 h-full">
          <div className="mb-3 flex-1">
            <h3>
              <a className="text-2xl font-light text-blue-700" href={url}>
                {name}
              </a>
            </h3>
          </div>
          {address && (
            <div className="text-sm lg:text-base text-black font-light mb-2 lg:mb-6">
              {address}
            </div>
          )}

          {Array.isArray(bedrooms) && bedrooms.length > 0 && (
            <div className="mb-6 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="14"
                fill="none"
                className="mr-3 text-blue-700"
                viewBox="0 0 22 14"
              >
                <path
                  fill="currentColor"
                  d="M4.3 3.9c-.2 0-.4.2-.4.4v2h4.7v-2c0-.2-.1-.4-.3-.4h-4ZM2 13.6c0 .2.1.4.4.4h3.1c.2 0 .4-.2.4-.4v-1.2h-4v1.2Z"
                />
                <path
                  fill="currentColor"
                  d="M3.1 4.3c0-.7.6-1.2 1.2-1.2h4c.6 0 1.1.5 1.1 1.2v2h3.2v-2c0-.7.5-1.2 1.1-1.2h4c.6 0 1.2.5 1.2 1.2v2h1.5V1.1C20.4.5 20 0 19.2 0H2.7c-.6 0-1.1.5-1.1 1.2v5H3v-2Z"
                />
                <path
                  fill="currentColor"
                  d="M13.7 3.9c-.2 0-.3.2-.3.4v2H18v-2c0-.2-.2-.4-.4-.4h-4Zm2.4 9.7c0 .2.2.4.4.4h3.1c.3 0 .4-.2.4-.4v-1.2h-3.9v1.2ZM20.8 7H1.2C.5 7 0 7.5 0 8.2v3c0 .3.2.5.4.5h21.2c.2 0 .4-.2.4-.4V8.2c0-.7-.5-1.2-1.2-1.2Z"
                />
              </svg>

              <div className="text-black text-sm font-light">
                {bedrooms.map((option, idx) => (
                  <span key={idx}>
                    {idx > 0
                      ? idx === bedrooms.length - 1
                        ? ' & '
                        : ', '
                      : ''}
                    {option.number_of_bedrooms}
                  </span>
                ))}{' '}
                bedrooms
              </div>
            </div>
          )}

          {min_price !== '0' || max_price !== '0' ? (
            <div className="text-black mb-6 font-light">
              {min_price !== '0' && (
                <>
                  From <span>&pound;{min_price}</span>
                </>
              )}
            </div>
          ) : null}

          <div className="flex flex-col lg:flex-row lg:items-center">
            <a
              href={url}
              className="btn btn--blue btn--sml mr-0 mb-5 lg:mr-5 lg:mb-0"
            >
              <svg className="w-[7px] h-[10px]">
                <use xlinkHref="#icon-chevron" />
              </svg>
              <span>
                View <span className="sr-only">{name}</span> development
              </span>
            </a>

            <button
              data-overlay="enquiry"
              data-development-code={coins_id}
              data-development={name}
              className="js-overlay-open cursor-pointer btn btn--grey-light btn--sml"
            >
              <svg className="w-[7px] h-[10px]">
                <use xlinkHref="#icon-plus" />
              </svg>
              <span>
                Make an enquiry <span className="sr-only">for {name}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DevelopmentCard;
